const storagePrefix = "chew:lkg:";

interface StoredSnapshot<T> {
  storedAt: string;
  value: T;
}

// Scoped to the signed-in login so a shared Mac never serves one engineer's queue to the next, and
// cleared outright on a 401. This is the same reason the service worker refuses to cache /api/*.
function storageKey(login: string, name: string): string {
  return `${storagePrefix}${login}:${name}`;
}

export function rememberLastKnownGood<T>(login: string, name: string, value: T): void {
  try {
    window.localStorage.setItem(
      storageKey(login, name),
      JSON.stringify({ storedAt: new Date().toISOString(), value } satisfies StoredSnapshot<T>),
    );
  } catch {
    // A full or unavailable quota is not worth failing a successful request over.
  }
}

export function readLastKnownGood<T>(login: string, name: string): StoredSnapshot<T> | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey(login, name));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<StoredSnapshot<T>>;
    if (!parsed || parsed.value === undefined || !parsed.storedAt) return undefined;
    return { storedAt: parsed.storedAt, value: parsed.value };
  } catch {
    return undefined;
  }
}

export function forgetLastKnownGood(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(storagePrefix)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Nothing useful to do if storage is unavailable.
  }
}
